import React, { forwardRef, useEffect, useState } from "react";
import upperFirst from "lodash/upperFirst";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import AddIcon from "@mui/icons-material/Add";

import { isAutocompleteInputClear } from "../utils/autocompleteClearReason";

function AddNewPaper({ onAddNew, addNewLabel, children, ...paperProps }) {
  return (
    <Paper {...paperProps}>
      {children}
      <Divider />
      <Box
        sx={{ p: 1 }}
        onMouseDown={e => e.preventDefault()}
      >
        <Button
          fullWidth
          startIcon={<AddIcon />}
          onClick={onAddNew}
          sx={{ justifyContent: "flex-start", textTransform: "none" }}
        >
          {addNewLabel}
        </Button>
      </Box>
    </Paper>
  );
}

export default function EntitySelect({
  label,
  name = "",
  value,
  onChange,
  suggestions = [],
  disabled = false,
  getOptionLabel = option => option.name || "",
  renderOption,
  onAddNew,
  addNewLabel = "Add New",
}) {
  const selectedEntity = suggestions.find(e => e.id === value) || null;
  const selectedLabel = selectedEntity ? getOptionLabel(selectedEntity) : "";
  const [inputValue, setInputValue] = useState(selectedLabel);

  useEffect(() => {
    setInputValue(selectedLabel);
  }, [selectedLabel, value]);

  const filterOptions = (options, { inputValue }) => {
    if (!inputValue) return options;

    const searchTerm = inputValue.toLowerCase();
    return options.filter(option => {
      const optionLabel = String(getOptionLabel(option) || "").toLowerCase();
      return optionLabel.includes(searchTerm);
    });
  };

  const PaperComponent = onAddNew
    ? forwardRef((props, ref) => (
        <AddNewPaper ref={ref} {...props} onAddNew={onAddNew} addNewLabel={addNewLabel} />
      ))
    : undefined;

  return (
    <Autocomplete
      options={suggestions}
      getOptionLabel={getOptionLabel}
      value={selectedEntity}
      inputValue={inputValue}
      selectOnFocus
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "reset") {
          return;
        }

        if (reason === "blur") {
          setInputValue(selectedLabel);
          return;
        }

        setInputValue(newInputValue);
      }}
      onChange={(event, newValue, reason) => {
        setInputValue(newValue ? getOptionLabel(newValue) : "");
        if (!isAutocompleteInputClear(reason, event)) {
          onChange(newValue, reason, event);
        }
      }}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      getOptionKey={option => option?.id ?? getOptionLabel(option)}
      filterOptions={filterOptions}
      PaperComponent={PaperComponent}
      renderOption={renderOption}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          name={name}
          variant="outlined"
          size="small"
          data-id={`select${upperFirst(name)}`}
          fullWidth
        />
      )}
    />
  );
}
